// Team Members Management Page - Invite/manage helpdesk staff
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  MoreVert,
  PersonAdd,
  Edit,
  Delete,
  Email,
  ContentCopy,
} from '@mui/icons-material';
import { DataTable, type Column } from '@/components/common/DataTable';
import { useOrganization } from '@/contexts/OrganizationContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { organizationsService } from '@/services/collections/organizations';
import { formatRelativeTime } from '@/services/firebase/firestore';
import type { OrganizationMember, UserRole } from '@/types';

const ROLE_OPTIONS: { value: Exclude<UserRole, 'super_admin'>; label: string; description: string }[] = [
  { value: 'owner', label: 'Owner', description: 'Full access including billing and settings' },
  { value: 'admin', label: 'Admin', description: 'Manage team, settings, and all content' },
  { value: 'agent', label: 'Agent', description: 'Handle tickets and manage knowledge base' },
  { value: 'viewer', label: 'Viewer', description: 'View-only access to tickets and analytics' },
];

export const TeamPage = () => {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { user } = useAuth();
  const { canManageMembers } = usePermissions();
  const { showSuccess, showError } = useToast();

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tab, setTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; member: OrganizationMember } | null>(null);

  // Invite dialog state
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Exclude<UserRole, 'super_admin'>>('agent');
  const [inviting, setInviting] = useState(false);

  // Edit role dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [editMember, setEditMember] = useState<OrganizationMember | null>(null);
  const [editRole, setEditRole] = useState<Exclude<UserRole, 'super_admin'>>('agent');

  // Fetch members
  const fetchMembers = async () => {
    if (!organization) return;

    setLoading(true);
    try {
      const data = await organizationsService.getMembers(organization.id);
      setMembers(data);
    } catch (error) {
      console.error('Error fetching members:', error);
      showError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [organization]);

  // Filter members
  const filteredMembers = members.filter((member) => {
    if (tab === 1 && member.status !== 'active') return false;
    if (tab === 2 && member.status !== 'pending') return false;
    if (tab === 3 && member.status !== 'inactive') return false;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        member.email.toLowerCase().includes(query) ||
        member.displayName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Invite new member
  const handleInvite = async () => {
    if (!organization || !inviteEmail) return;

    setInviting(true);
    try {
      // Create a pending member record
      // In production, this would send an email invitation
      await organizationsService.addMember(organization.id, `pending_${Date.now()}`, {
        email: inviteEmail,
        displayName: inviteEmail.split('@')[0],
        role: inviteRole,
        invitedAt: new Date() as any,
        status: 'pending',
      });

      showSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteDialog(false);
      setInviteEmail('');
      setInviteRole('agent');
      fetchMembers();
    } catch (error) {
      console.error('Error inviting member:', error);
      showError('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  // Update member role
  const handleUpdateRole = async () => {
    if (!organization || !editMember) return;

    try {
      await organizationsService.updateMemberRole(organization.id, editMember.userId, editRole);
      showSuccess('Role updated successfully');
      setEditDialog(false);
      setEditMember(null);
      fetchMembers();
    } catch (error) {
      console.error('Error updating role:', error);
      showError('Failed to update role');
    }
  };

  // Remove member
  const handleRemove = async (member: OrganizationMember) => {
    if (!organization) return;
    if (!window.confirm(`Are you sure you want to remove ${member.displayName || member.email}?`)) return;

    try {
      await organizationsService.removeMember(organization.id, member.userId);
      showSuccess('Member removed');
      fetchMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      showError('Failed to remove member');
    }
    setMenuAnchor(null);
  };

  // Resend invitation
  const handleResendInvite = (member: OrganizationMember) => {
    // In production, this would resend the email
    showSuccess(`Invitation resent to ${member.email}`);
    setMenuAnchor(null);
  };

  // Copy invite link
  const handleCopyInviteLink = () => {
    const inviteLink = `${window.location.origin}/invite/${organization?.id}`;
    navigator.clipboard.writeText(inviteLink);
    showSuccess('Invite link copied to clipboard');
  };

  // Table columns
  const columns: Column<OrganizationMember>[] = [
    {
      id: 'member',
      label: 'Member',
      render: (member) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
            {(member.displayName?.[0] || member.email[0]).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {member.displayName || 'Pending User'}
              {member.userId === user?.uid && (
                <Chip label="You" size="small" sx={{ ml: 1, height: 20 }} />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {member.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'role',
      label: 'Role',
      render: (member) => {
        const roleConfig = ROLE_OPTIONS.find((r) => r.value === member.role);
        return (
          <Chip
            label={roleConfig?.label || member.role}
            size="small"
            color={member.role === 'owner' ? 'primary' : 'default'}
            variant={member.role === 'owner' ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      render: (member) => (
        <Chip
          label={member.status}
          size="small"
          color={
            member.status === 'active' ? 'success' :
            member.status === 'pending' ? 'warning' : 'default'
          }
        />
      ),
    },
    {
      id: 'joined',
      label: 'Joined',
      render: (member) => (
        <Typography variant="body2" color="text.secondary">
          {member.acceptedAt ? formatRelativeTime(member.acceptedAt) : 'Pending'}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: '',
      render: (member) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor({ el: e.currentTarget, member });
          }}
          disabled={member.userId === organization?.ownerId && member.userId !== user?.uid}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Team
          </Typography>
          <Typography color="text.secondary">
            Manage your helpdesk staff and their permissions
          </Typography>
        </Box>
        {canManageMembers && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={handleCopyInviteLink}
            >
              Copy Invite Link
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setInviteDialog(true)}
            >
              Invite Member
            </Button>
          </Stack>
        )}
      </Box>

      {/* Plan Limit Warning */}
      {organization && members.filter((m) => m.status === 'active').length >= organization.limits.seats && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You've reached your seat limit ({organization.limits.seats} seats).
          <Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/app/settings', { state: { upgrade: true } })}>Upgrade Plan</Button>
        </Alert>
      )}

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              {members.filter((m) => m.status === 'active').length}
            </Typography>
            <Typography color="text.secondary">Active Members</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              {members.filter((m) => m.status === 'pending').length}
            </Typography>
            <Typography color="text.secondary">Pending Invites</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              {organization?.limits.seats === -1 ? '∞' : organization?.limits.seats}
            </Typography>
            <Typography color="text.secondary">Seat Limit</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`All (${members.length})`} />
        <Tab label={`Active (${members.filter((m) => m.status === 'active').length})`} />
        <Tab label={`Pending (${members.filter((m) => m.status === 'pending').length})`} />
        <Tab label={`Inactive (${members.filter((m) => m.status === 'inactive').length})`} />
      </Tabs>

      {/* Search */}
      <TextField
        placeholder="Search members..."
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, width: 300 }}
      />

      {/* Members Table */}
      <DataTable
        columns={columns}
        data={filteredMembers}
        loading={loading}
        emptyMessage="No team members found"
      />

      {/* Member Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {menuAnchor?.member.status === 'pending' && (
          <MenuItem onClick={() => menuAnchor && handleResendInvite(menuAnchor.member)}>
            <Email sx={{ mr: 1 }} fontSize="small" /> Resend Invite
          </MenuItem>
        )}
        {canManageMembers && menuAnchor?.member.userId !== organization?.ownerId && (
          <MenuItem
            onClick={() => {
              if (menuAnchor) {
                setEditMember(menuAnchor.member);
                setEditRole(menuAnchor.member.role);
                setEditDialog(true);
                setMenuAnchor(null);
              }
            }}
          >
            <Edit sx={{ mr: 1 }} fontSize="small" /> Change Role
          </MenuItem>
        )}
        {canManageMembers && menuAnchor?.member.userId !== user?.uid && (
          <MenuItem
            onClick={() => menuAnchor && handleRemove(menuAnchor.member)}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} fontSize="small" /> Remove
          </MenuItem>
        )}
      </Menu>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => setInviteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@company.com"
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteRole}
                label="Role"
                onChange={(e) => setInviteRole(e.target.value as Exclude<UserRole, 'super_admin'>)}
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {role.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info">
              An email invitation will be sent. The invitee will need to create an account
              or sign in to accept the invitation.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={!inviteEmail || inviting}
            startIcon={<PersonAdd />}
          >
            {inviting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Changing role for: <strong>{editMember?.displayName || editMember?.email}</strong>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>New Role</InputLabel>
              <Select
                value={editRole}
                label="New Role"
                onChange={(e) => setEditRole(e.target.value as Exclude<UserRole, 'super_admin'>)}
              >
                {ROLE_OPTIONS.filter((r) => r.value !== 'owner').map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {role.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateRole}>
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
